import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import {
  Camera, Plus, Search, Loader2, Edit2, Trash2, ArrowUpRight, ArrowDownLeft, Settings2,
  Video, Aperture, Lamp, Mic, Move3d, BatteryCharging, Cable, Plane, Laptop, Box,
  ImageIcon, Download, X, AlertTriangle, Clock, Euro, Paperclip, FileCheck, FileText,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { useAuth } from "../context/AuthContext";
import {
  getEquipmentCategories, createEquipmentCategory, deleteEquipmentCategory,
  getEquipment, createEquipment, updateEquipment, deleteEquipment, getEquipmentStats,
  checkoutEquipment, returnEquipmentMovement, getEquipmentMovements,
  getMovementPhotos, uploadMovementPhoto, downloadMovementPhoto, deleteMovementPhoto,
  getEmployees, getCustomers, createRentalQuote,
} from "../services/api";

const CAT_ICONS = { video: Video, aperture: Aperture, lamp: Lamp, mic: Mic, "move-3d": Move3d, "battery-charging": BatteryCharging, cable: Cable, plane: Plane, laptop: Laptop, box: Box };
const CatIcon = ({ icon, ...props }) => { const I = CAT_ICONS[icon] || Box; return <I {...props} />; };

const CONDITIONS = [
  { value: "neuf", label: "Neuf", color: "#10B981" },
  { value: "bon", label: "Bon état", color: "#26788A" },
  { value: "use", label: "Usé", color: "#F59E0B" },
  { value: "panne", label: "En panne", color: "#EF4444" },
];
const REASONS = { tournage: "Tournage", location: "Location client", pret: "Prêt", reparation: "Réparation", autre: "Autre" };
const RETURN_CONDITIONS = [
  { value: "ok", label: "RAS — bon état", color: "#10B981" },
  { value: "damaged", label: "Abîmé", color: "#F59E0B" },
  { value: "lost", label: "Perdu", color: "#EF4444" },
];

const fmtDate = (d) => (d ? new Date(d + "T00:00:00").toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" }) : "—");
const fmtEuro = (n) => (n ?? 0).toLocaleString("fr-FR", { style: "currency", currency: "EUR" });
const todayIso = () => new Date().toISOString().split("T")[0];

const resizeImage = (file) => new Promise((resolve, reject) => {
  const img = new Image();
  img.onload = () => {
    const max = 700;
    const scale = Math.min(1, max / Math.max(img.width, img.height));
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(img.width * scale);
    canvas.height = Math.round(img.height * scale);
    canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
    resolve(canvas.toDataURL("image/jpeg", 0.8));
  };
  img.onerror = reject;
  img.src = URL.createObjectURL(file);
});

const emptyItem = { name: "", category_id: "", brand_model: "", serial_number: "", quantity: 1, condition: "bon", purchase_value: "", daily_rate: "", notes: "", photo: null };

export default function EquipmentPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const canSeePrices = user?.role === "admin" || !!user?.can_view_equipment_prices;

  const [tab, setTab] = useState("inventory");
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [stats, setStats] = useState(null);
  const [movements, setMovements] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [catFilter, setCatFilter] = useState("all");
  const [saving, setSaving] = useState(false);

  const [itemDialog, setItemDialog] = useState(null); // {item?} null=closed, {}=create
  const [form, setForm] = useState(emptyItem);
  const [catDialog, setCatDialog] = useState(false);
  const [newCat, setNewCat] = useState("");
  const [checkoutItem, setCheckoutItem] = useState(null);
  const [checkoutForm, setCheckoutForm] = useState({});
  const [returnMov, setReturnMov] = useState(null);
  const [returnForm, setReturnForm] = useState({ condition: "ok", note: "", files: [] });
  const [photosMov, setPhotosMov] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [photoUrls, setPhotoUrls] = useState({});

  const load = useCallback(async () => {
    try {
      const [itemsRes, catsRes, statsRes] = await Promise.all([
        getEquipment({ q: search || undefined, category_id: catFilter !== "all" ? catFilter : undefined }),
        getEquipmentCategories(),
        getEquipmentStats(),
      ]);
      setItems(itemsRes.data);
      setCategories(catsRes.data);
      setStats(statsRes.data);
    } catch {
      toast.error("Erreur de chargement du matériel");
    } finally {
      setLoading(false);
    }
  }, [search, catFilter]);

  const loadMovements = useCallback(async (active) => {
    try {
      const res = await getEquipmentMovements(active !== undefined ? { active } : {});
      setMovements(res.data);
    } catch {
      toast.error("Erreur de chargement des mouvements");
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (tab === "out") loadMovements(true);
    if (tab === "history") loadMovements(false);
  }, [tab, loadMovements]);
  useEffect(() => {
    getEmployees().then((r) => setEmployees((r.data || []).filter((e) => e.is_active !== false))).catch(() => {});
    getCustomers().then((r) => setCustomers(r.data || [])).catch(() => {});
  }, []);

  const openCreate = () => { setForm({ ...emptyItem, category_id: categories[0]?.id || "" }); setItemDialog({}); };
  const openEdit = (it) => {
    setForm({ name: it.name, category_id: it.category_id, brand_model: it.brand_model || "", serial_number: it.serial_number || "", quantity: it.quantity, condition: it.condition, purchase_value: it.purchase_value ?? "", daily_rate: it.daily_rate ?? "", notes: it.notes || "", photo: it.photo || null });
    setItemDialog({ item: it });
  };

  const handlePhotoChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    try {
      const dataUrl = await resizeImage(file);
      setForm((f) => ({ ...f, photo: dataUrl }));
    } catch {
      toast.error("Impossible de lire l'image");
    }
  };

  const saveItem = async () => {
    if (!form.name.trim()) { toast.error("Nom requis"); return; }
    if (!form.category_id) { toast.error("Catégorie requise"); return; }
    const payload = {
      name: form.name, category_id: form.category_id, brand_model: form.brand_model || null,
      serial_number: form.serial_number || null, quantity: Number(form.quantity) || 0,
      condition: form.condition, notes: form.notes || null, photo: form.photo,
    };
    if (canSeePrices) {
      payload.purchase_value = form.purchase_value === "" ? null : Number(form.purchase_value);
      payload.daily_rate = form.daily_rate === "" ? null : Number(form.daily_rate);
    }
    try {
      setSaving(true);
      if (itemDialog?.item) await updateEquipment(itemDialog.item.id, payload);
      else await createEquipment(payload);
      toast.success(itemDialog?.item ? "Matériel modifié" : "Matériel ajouté");
      setItemDialog(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  const removeItem = async (it) => {
    if (!window.confirm(`Supprimer « ${it.name} » de l'inventaire ?\n(Matériel plus en votre possession — l'historique sera aussi supprimé)`)) return;
    try {
      await deleteEquipment(it.id);
      toast.success("Matériel supprimé");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const addCategory = async () => {
    if (!newCat.trim()) return;
    try {
      await createEquipmentCategory({ name: newCat.trim() });
      setNewCat("");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };

  const removeCategory = async (c) => {
    try {
      await deleteEquipmentCategory(c.id);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };

  const openCheckout = (it) => {
    setCheckoutForm({ reason: "tournage", quantity: 1, employee_id: "none", customer_id: "none", out_date: todayIso(), expected_return_date: "", note: "" });
    setCheckoutItem(it);
  };

  const doCheckout = async () => {
    try {
      setSaving(true);
      await checkoutEquipment(checkoutItem.id, {
        reason: checkoutForm.reason,
        quantity: Number(checkoutForm.quantity) || 1,
        employee_id: checkoutForm.employee_id === "none" ? null : checkoutForm.employee_id,
        customer_id: checkoutForm.customer_id === "none" ? null : checkoutForm.customer_id,
        out_date: checkoutForm.out_date,
        expected_return_date: checkoutForm.expected_return_date || null,
        note: checkoutForm.note || null,
      });
      toast.success("Sortie enregistrée");
      setCheckoutItem(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  const doReturn = async () => {
    try {
      setSaving(true);
      await returnEquipmentMovement(returnMov.id, { condition: returnForm.condition, note: returnForm.note || null });
      for (const file of returnForm.files) {
        const fd = new FormData();
        fd.append("file", file);
        await uploadMovementPhoto(returnMov.id, fd);
      }
      toast.success("Retour enregistré");
      setReturnMov(null);
      setReturnForm({ condition: "ok", note: "", files: [] });
      load();
      loadMovements(tab === "history" ? false : true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  const openPhotos = async (m) => {
    setPhotosMov(m);
    setPhotos([]);
    setPhotoUrls({});
    try {
      const res = await getMovementPhotos(m.id);
      setPhotos(res.data);
      for (const f of res.data) {
        downloadMovementPhoto(f.id).then((r) => {
          setPhotoUrls((prev) => ({ ...prev, [f.id]: URL.createObjectURL(r.data) }));
        }).catch(() => {});
      }
    } catch { toast.error("Erreur de chargement des photos"); }
  };

  const downloadPhoto = async (f) => {
    try {
      const res = await downloadMovementPhoto(f.id);
      const url = URL.createObjectURL(res.data);
      const link = document.createElement("a");
      link.href = url; link.download = f.filename; link.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Erreur de téléchargement"); }
  };

  const custName = (c) => c.company_name || `${c.first_name || ""} ${c.last_name || ""}`.trim();

  const makeQuote = async (m) => {
    if (!window.confirm(`Créer un devis de location pour « ${m.equipment_name} » (client : ${m.customer_name || "—"}) ?`)) return;
    try {
      const res = await createRentalQuote(m.id);
      toast.success(res.data.quote_number ? `Devis ${res.data.quote_number} créé` : "Devis créé (brouillon)");
      navigate(`/document/${res.data.quote_id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de la création du devis");
    }
  };

  const MovementRow = ({ m, showReturn }) => (
    <div className={`bg-white border rounded-xl px-4 py-3 ${m.is_overdue ? "border-red-200 bg-red-50/40" : "border-slate-200"}`} data-testid={`movement-${m.id}`}>
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex-1 min-w-[180px]">
          <p className="text-sm font-semibold text-slate-900">{m.equipment_name} <span className="text-slate-400 font-normal">× {m.quantity}</span></p>
          <p className="text-xs text-slate-400 mt-0.5">
            {REASONS[m.reason] || m.reason}
            {m.employee_name && ` · ${m.employee_name}`}
            {m.customer_name && ` · ${m.customer_name}`}
          </p>
        </div>
        <div className="text-xs text-slate-500">
          <p>Sortie : <span className="font-medium text-slate-700">{fmtDate(m.out_date)}</span></p>
          {m.status === "out" ? (
            <p className={m.is_overdue ? "text-red-500 font-semibold" : ""}>
              Retour prévu : {fmtDate(m.expected_return_date)} {m.is_overdue && "⟵ en retard"}
            </p>
          ) : (
            <p>Retour : <span className="font-medium text-slate-700">{m.returned_at ? new Date(m.returned_at).toLocaleDateString("fr-FR") : "—"}</span>
              {m.return_condition && (
                <span className="ml-1.5 font-medium" style={{ color: RETURN_CONDITIONS.find((r) => r.value === m.return_condition)?.color }}>
                  · {RETURN_CONDITIONS.find((r) => r.value === m.return_condition)?.label}
                </span>
              )}
            </p>
          )}
        </div>
        <div className="flex items-center gap-1.5 ml-auto">
          {m.reason === "location" && m.customer_id && !m.quote_id && (
            <Button size="sm" variant="outline" className="h-8 text-[#26788A] border-[#26788A]/30 hover:bg-[#26788A]/5" onClick={() => makeQuote(m)} data-testid={`create-quote-btn-${m.id}`}>
              <FileText size={13} className="mr-1" /> Créer un devis
            </Button>
          )}
          {m.quote_id && (
            <Button size="sm" variant="ghost" className="h-8 text-[#26788A] hover:text-[#1D5E6C]" onClick={() => navigate(`/document/${m.quote_id}`)} data-testid={`view-quote-btn-${m.id}`}>
              <FileCheck size={13} className="mr-1" /> {m.quote_number || "Devis"}
            </Button>
          )}
          {m.photos_count > 0 && (
            <Button size="sm" variant="outline" className="h-8 text-slate-500" onClick={() => openPhotos(m)} data-testid={`view-photos-${m.id}`}>
              <ImageIcon size={13} className="mr-1" /> {m.photos_count} photo{m.photos_count > 1 ? "s" : ""}
            </Button>
          )}
          {showReturn && m.status === "out" && (
            <Button size="sm" className="h-8 bg-[#26788A] hover:bg-[#1D5E6C] text-white" onClick={() => { setReturnForm({ condition: "ok", note: "", files: [] }); setReturnMov(m); }} data-testid={`return-btn-${m.id}`}>
              <ArrowDownLeft size={13} className="mr-1" /> Retour
            </Button>
          )}
        </div>
      </div>
      {(m.note || m.return_note) && <p className="text-[11px] text-slate-400 mt-1.5">{m.note}{m.note && m.return_note ? " · " : ""}{m.return_note && `Retour : ${m.return_note}`}</p>}
    </div>
  );

  return (
    <div className="p-4 lg:p-8 max-w-6xl mx-auto overflow-y-auto h-screen pb-24" data-testid="equipment-page">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Camera size={24} className="text-[#26788A]" /> Matériel
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">Inventaire du matériel audiovisuel, entrées et sorties</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setCatDialog(true)} className="btn-outline" data-testid="manage-categories-btn">
            <Settings2 size={15} className="mr-1.5" /> Catégories
          </Button>
          <Button onClick={openCreate} className="btn-primary" data-testid="add-equipment-btn">
            <Plus size={15} className="mr-1.5" /> Ajouter du matériel
          </Button>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5" data-testid="equipment-stats">
          <div className="bg-white border border-slate-200 rounded-xl p-3.5">
            <p className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold">Articles</p>
            <p className="text-xl font-bold text-slate-900 font-numbers mt-0.5">{stats.items_count} <span className="text-xs font-normal text-slate-400">({stats.total_quantity} unités)</span></p>
          </div>
          <div className="bg-white border border-slate-200 rounded-xl p-3.5">
            <p className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold flex items-center gap-1"><ArrowUpRight size={11} /> Sorties en cours</p>
            <p className="text-xl font-bold text-[#26788A] font-numbers mt-0.5">{stats.active_movements}</p>
          </div>
          <div className="bg-white border border-slate-200 rounded-xl p-3.5">
            <p className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold flex items-center gap-1"><Clock size={11} /> Retards</p>
            <p className={`text-xl font-bold font-numbers mt-0.5 ${stats.overdue_movements > 0 ? "text-red-500" : "text-slate-900"}`}>{stats.overdue_movements}</p>
          </div>
          {canSeePrices && stats.total_value != null && (
            <div className="bg-white border border-slate-200 rounded-xl p-3.5" data-testid="stat-total-value">
              <p className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold flex items-center gap-1"><Euro size={11} /> Valeur du parc</p>
              <p className="text-xl font-bold text-slate-900 font-numbers mt-0.5">{fmtEuro(stats.total_value)}</p>
            </div>
          )}
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 bg-slate-100 p-1 rounded-lg mb-4 w-fit">
        {[["inventory", "Inventaire"], ["out", "Sorties en cours"], ["history", "Historique"]].map(([v, l]) => (
          <button key={v} className={`px-4 py-2 rounded-md text-sm font-medium ${tab === v ? "bg-white text-[#26788A] shadow-sm" : "text-slate-600"}`} onClick={() => setTab(v)} data-testid={`equipment-tab-${v}`}>{l}</button>
        ))}
      </div>

      {tab === "inventory" && (
        <>
          <div className="flex flex-wrap gap-2 mb-4">
            <div className="relative flex-1 min-w-[220px] max-w-sm">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Rechercher (nom, marque, n° série)…" className="pl-9 input-field" data-testid="equipment-search" />
            </div>
            <div className="flex gap-1.5 flex-wrap items-center">
              <button className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${catFilter === "all" ? "bg-[#26788A] text-white border-[#26788A]" : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"}`} onClick={() => setCatFilter("all")} data-testid="cat-filter-all">
                Tout
              </button>
              {categories.filter((c) => c.items_count > 0 || catFilter === c.id).map((c) => (
                <button key={c.id} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${catFilter === c.id ? "bg-[#26788A] text-white border-[#26788A]" : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"}`} onClick={() => setCatFilter(c.id)} data-testid={`cat-filter-${c.id}`}>
                  <CatIcon icon={c.icon} size={12} /> {c.name} <span className="opacity-60">({c.items_count})</span>
                </button>
              ))}
            </div>
          </div>

          {loading ? (
            <div className="flex justify-center py-16"><Loader2 size={26} className="animate-spin text-slate-300" /></div>
          ) : items.length === 0 ? (
            <div className="text-center py-16 bg-white border border-dashed border-slate-200 rounded-xl">
              <Camera size={30} className="mx-auto text-slate-300 mb-2" />
              <p className="text-slate-500 text-sm">Aucun matériel{search ? " trouvé" : " dans l'inventaire"}.</p>
              {!search && <button className="text-xs font-medium text-[#26788A] hover:underline mt-1" onClick={openCreate}>Ajouter votre premier matériel</button>}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="equipment-grid">
              {items.map((it) => {
                const cat = categories.find((c) => c.id === it.category_id);
                const cond = CONDITIONS.find((c) => c.value === it.condition) || CONDITIONS[1];
                return (
                  <div key={it.id} className="bg-white border border-slate-200 rounded-xl overflow-hidden hover:border-slate-300 transition-colors" data-testid={`equipment-card-${it.id}`}>
                    <div className="h-36 bg-slate-100 flex items-center justify-center relative">
                      {it.photo ? (
                        <img src={it.photo} alt={it.name} className="w-full h-full object-cover" />
                      ) : (
                        <CatIcon icon={cat?.icon} size={34} className="text-slate-300" />
                      )}
                      {it.has_overdue && (
                        <span className="absolute top-2 right-2 flex items-center gap-1 text-[10px] font-bold text-white bg-red-500 px-2 py-0.5 rounded-full">
                          <AlertTriangle size={10} /> Retard
                        </span>
                      )}
                    </div>
                    <div className="p-3.5">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-slate-900 truncate">{it.name}</p>
                          <p className="text-xs text-slate-400 truncate">{it.brand_model || cat?.name}{it.serial_number ? ` · ${it.serial_number}` : ""}</p>
                        </div>
                        <span className="flex items-center gap-1 text-[10px] font-medium shrink-0" style={{ color: cond.color }}>
                          <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: cond.color }}></span>{cond.label}
                        </span>
                      </div>
                      <div className="flex items-center justify-between mt-2.5">
                        <p className="text-xs font-numbers">
                          <span className={`font-bold ${it.available === 0 ? "text-red-500" : "text-[#26788A]"}`}>{it.available} dispo</span>
                          <span className="text-slate-400"> / {it.quantity}</span>
                          {it.out_quantity > 0 && <span className="text-amber-600 ml-1.5">({it.out_quantity} sorti{it.out_quantity > 1 ? "s" : ""})</span>}
                        </p>
                        {canSeePrices && it.daily_rate != null && (
                          <span className="text-[11px] font-medium text-slate-500 font-numbers" data-testid={`daily-rate-${it.id}`}>{fmtEuro(it.daily_rate)}/j</span>
                        )}
                      </div>
                      <div className="flex items-center gap-1.5 mt-3 pt-2.5 border-t border-slate-100">
                        <Button size="sm" className="h-8 flex-1 bg-[#26788A] hover:bg-[#1D5E6C] text-white" disabled={it.available === 0} onClick={() => openCheckout(it)} data-testid={`checkout-btn-${it.id}`}>
                          <ArrowUpRight size={13} className="mr-1" /> Sortie
                        </Button>
                        <Button size="sm" variant="ghost" className="h-8 text-slate-500" onClick={() => openEdit(it)} data-testid={`edit-equipment-${it.id}`}><Edit2 size={14} /></Button>
                        <Button size="sm" variant="ghost" className="h-8 text-slate-400 hover:text-red-500" onClick={() => removeItem(it)} data-testid={`delete-equipment-${it.id}`}><Trash2 size={14} /></Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {(tab === "out" || tab === "history") && (
        <div className="space-y-2.5" data-testid={`movements-${tab}`}>
          {movements.length === 0 ? (
            <div className="text-center py-14 bg-white border border-dashed border-slate-200 rounded-xl">
              <p className="text-slate-400 text-sm">{tab === "out" ? "Aucune sortie en cours — tout le matériel est au studio." : "Aucun mouvement dans l'historique."}</p>
            </div>
          ) : (
            movements.map((m) => <MovementRow key={m.id} m={m} showReturn={tab === "out"} />)
          )}
        </div>
      )}

      {/* Dialog fiche matériel */}
      <Dialog open={!!itemDialog} onOpenChange={(v) => !v && setItemDialog(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" aria-describedby={undefined} data-testid="equipment-dialog">
          <DialogHeader><DialogTitle>{itemDialog?.item ? "Modifier le matériel" : "Ajouter du matériel"}</DialogTitle></DialogHeader>
          <div className="space-y-3.5 py-1">
            <div className="flex items-center gap-3">
              <label className="w-24 h-24 rounded-xl bg-slate-100 border border-dashed border-slate-300 flex items-center justify-center cursor-pointer overflow-hidden hover:border-[#26788A] transition-colors shrink-0">
                {form.photo ? <img src={form.photo} alt="" className="w-full h-full object-cover" /> : <Camera size={20} className="text-slate-400" />}
                <input type="file" accept="image/*" className="hidden" onChange={handlePhotoChange} data-testid="equipment-photo-input" />
              </label>
              <div className="flex-1 space-y-2">
                <div>
                  <Label className="text-xs">Nom</Label>
                  <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Ex : Sony FX3" className="mt-1" data-testid="equipment-name-input" />
                </div>
                {form.photo && <button className="text-[11px] text-red-400 hover:underline" onClick={() => setForm({ ...form, photo: null })}>Retirer la photo</button>}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Catégorie</Label>
                <Select value={form.category_id} onValueChange={(v) => setForm({ ...form, category_id: v })}>
                  <SelectTrigger className="mt-1" data-testid="equipment-category-select"><SelectValue placeholder="Choisir…" /></SelectTrigger>
                  <SelectContent>
                    {categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Marque / Modèle</Label>
                <Input value={form.brand_model} onChange={(e) => setForm({ ...form, brand_model: e.target.value })} className="mt-1" />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-xs">Quantité</Label>
                <Input type="number" min="0" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} className="mt-1" data-testid="equipment-quantity-input" />
              </div>
              <div>
                <Label className="text-xs">État</Label>
                <Select value={form.condition} onValueChange={(v) => setForm({ ...form, condition: v })}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CONDITIONS.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">N° de série</Label>
                <Input value={form.serial_number} onChange={(e) => setForm({ ...form, serial_number: e.target.value })} className="mt-1" />
              </div>
            </div>
            {canSeePrices && (
              <div className="grid grid-cols-2 gap-3" data-testid="equipment-prices-fields">
                <div>
                  <Label className="text-xs">Valeur d'achat (€)</Label>
                  <Input type="number" min="0" step="0.01" value={form.purchase_value} onChange={(e) => setForm({ ...form, purchase_value: e.target.value })} className="mt-1" data-testid="equipment-purchase-value" />
                </div>
                <div>
                  <Label className="text-xs">Tarif location / jour (€)</Label>
                  <Input type="number" min="0" step="0.01" value={form.daily_rate} onChange={(e) => setForm({ ...form, daily_rate: e.target.value })} className="mt-1" data-testid="equipment-daily-rate" />
                </div>
              </div>
            )}
            <div>
              <Label className="text-xs">Notes</Label>
              <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setItemDialog(null)}>Annuler</Button>
            <Button className="btn-primary" onClick={saveItem} disabled={saving} data-testid="save-equipment-btn">
              {saving && <Loader2 size={14} className="animate-spin mr-1" />} Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog catégories */}
      <Dialog open={catDialog} onOpenChange={setCatDialog}>
        <DialogContent className="max-w-sm" aria-describedby={undefined} data-testid="categories-dialog">
          <DialogHeader><DialogTitle>Catégories de matériel</DialogTitle></DialogHeader>
          <div className="space-y-1.5 max-h-[300px] overflow-y-auto">
            {categories.map((c) => (
              <div key={c.id} className="flex items-center gap-2.5 px-3 py-2 border border-slate-100 rounded-lg">
                <CatIcon icon={c.icon} size={15} className="text-[#26788A]" />
                <span className="text-sm text-slate-800 flex-1">{c.name}</span>
                <span className="text-[10px] text-slate-400">{c.items_count} art.</span>
                <button className="text-slate-300 hover:text-red-500 disabled:opacity-30" disabled={c.items_count > 0} onClick={() => removeCategory(c)} title={c.items_count > 0 ? "Catégorie utilisée" : "Supprimer"} data-testid={`delete-category-${c.id}`}>
                  <Trash2 size={13} />
                </button>
              </div>
            ))}
          </div>
          <div className="flex gap-2">
            <Input value={newCat} onChange={(e) => setNewCat(e.target.value)} placeholder="Nouvelle catégorie…" onKeyDown={(e) => e.key === "Enter" && addCategory()} data-testid="new-category-input" />
            <Button className="btn-primary" onClick={addCategory} data-testid="add-category-btn"><Plus size={15} /></Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog sortie */}
      <Dialog open={!!checkoutItem} onOpenChange={(v) => !v && setCheckoutItem(null)}>
        <DialogContent className="max-w-md" aria-describedby={undefined} data-testid="checkout-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><ArrowUpRight size={17} className="text-[#26788A]" /> Sortie — {checkoutItem?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-1">
            <p className="text-xs text-slate-400">{checkoutItem?.available} disponible(s) sur {checkoutItem?.quantity}</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Motif</Label>
                <Select value={checkoutForm.reason} onValueChange={(v) => setCheckoutForm({ ...checkoutForm, reason: v })}>
                  <SelectTrigger className="mt-1" data-testid="checkout-reason"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(REASONS).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Quantité</Label>
                <Input type="number" min="1" max={checkoutItem?.available} value={checkoutForm.quantity} onChange={(e) => setCheckoutForm({ ...checkoutForm, quantity: e.target.value })} className="mt-1" data-testid="checkout-quantity" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Emporté par (employé)</Label>
              <Select value={checkoutForm.employee_id} onValueChange={(v) => setCheckoutForm({ ...checkoutForm, employee_id: v })}>
                <SelectTrigger className="mt-1" data-testid="checkout-employee"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">—</SelectItem>
                  {employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.first_name} {e.last_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {checkoutForm.reason === "location" && (
              <div>
                <Label className="text-xs">Client (location)</Label>
                <Select value={checkoutForm.customer_id} onValueChange={(v) => setCheckoutForm({ ...checkoutForm, customer_id: v })}>
                  <SelectTrigger className="mt-1" data-testid="checkout-customer"><SelectValue /></SelectTrigger>
                  <SelectContent className="max-h-[240px]">
                    <SelectItem value="none">—</SelectItem>
                    {customers.map((c) => <SelectItem key={c.id} value={c.id}>{custName(c)}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Date de sortie</Label>
                <Input type="date" value={checkoutForm.out_date} onChange={(e) => setCheckoutForm({ ...checkoutForm, out_date: e.target.value })} className="mt-1" data-testid="checkout-out-date" />
              </div>
              <div>
                <Label className="text-xs">Retour prévu</Label>
                <Input type="date" value={checkoutForm.expected_return_date} onChange={(e) => setCheckoutForm({ ...checkoutForm, expected_return_date: e.target.value })} className="mt-1" data-testid="checkout-return-date" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Note (optionnel)</Label>
              <Input value={checkoutForm.note} onChange={(e) => setCheckoutForm({ ...checkoutForm, note: e.target.value })} className="mt-1" placeholder="Ex : tournage mariage Dupont" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCheckoutItem(null)}>Annuler</Button>
            <Button className="btn-primary" onClick={doCheckout} disabled={saving} data-testid="confirm-checkout-btn">
              {saving && <Loader2 size={14} className="animate-spin mr-1" />} Enregistrer la sortie
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog retour */}
      <Dialog open={!!returnMov} onOpenChange={(v) => !v && setReturnMov(null)}>
        <DialogContent className="max-w-md" aria-describedby={undefined} data-testid="return-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><ArrowDownLeft size={17} className="text-[#26788A]" /> Retour — {returnMov?.equipment_name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-1">
            <div>
              <Label className="text-xs">État au retour</Label>
              <div className="flex gap-2 mt-1.5">
                {RETURN_CONDITIONS.map((c) => (
                  <button key={c.value} onClick={() => setReturnForm({ ...returnForm, condition: c.value })}
                    className={`flex-1 px-2 py-2 rounded-lg text-xs font-medium border transition-colors ${returnForm.condition === c.value ? "text-white" : "bg-white text-slate-600 border-slate-200"}`}
                    style={returnForm.condition === c.value ? { backgroundColor: c.color, borderColor: c.color } : {}}
                    data-testid={`return-condition-${c.value}`}>
                    {c.label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-xs">Commentaire</Label>
              <Textarea rows={2} value={returnForm.note} onChange={(e) => setReturnForm({ ...returnForm, note: e.target.value })} className="mt-1" placeholder={returnForm.condition !== "ok" ? "Décrivez le problème constaté…" : "Optionnel"} data-testid="return-note" />
            </div>
            <div>
              <Label className="text-xs">Photos de l'état (consultables et téléchargeables)</Label>
              <label className="mt-1.5 flex items-center justify-center gap-2 border-2 border-dashed border-slate-200 hover:border-[#26788A] rounded-lg py-3 cursor-pointer text-sm text-slate-500 hover:text-[#26788A] transition-colors">
                <Paperclip size={14} /> Ajouter des photos ({returnForm.files.length} sélectionnée{returnForm.files.length > 1 ? "s" : ""})
                <input type="file" accept="image/*" multiple className="hidden" onChange={(e) => setReturnForm({ ...returnForm, files: [...returnForm.files, ...Array.from(e.target.files || [])] })} data-testid="return-photos-input" />
              </label>
              {returnForm.files.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {returnForm.files.map((f, i) => (
                    <span key={i} className="flex items-center gap-1 text-[10px] bg-slate-100 px-2 py-1 rounded-full text-slate-600">
                      {f.name}
                      <button onClick={() => setReturnForm({ ...returnForm, files: returnForm.files.filter((_, j) => j !== i) })}><X size={10} /></button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReturnMov(null)}>Annuler</Button>
            <Button className="btn-primary" onClick={doReturn} disabled={saving} data-testid="confirm-return-btn">
              {saving && <Loader2 size={14} className="animate-spin mr-1" />} Valider le retour
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog photos d'un mouvement */}
      <Dialog open={!!photosMov} onOpenChange={(v) => !v && setPhotosMov(null)}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" aria-describedby={undefined} data-testid="movement-photos-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><ImageIcon size={17} className="text-[#26788A]" /> Photos — {photosMov?.equipment_name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {photos.length === 0 && <p className="text-sm text-slate-400 text-center py-6">Aucune photo</p>}
            {photos.map((f) => (
              <div key={f.id} className="border border-slate-200 rounded-xl overflow-hidden" data-testid={`movement-photo-${f.id}`}>
                {photoUrls[f.id] ? (
                  <img src={photoUrls[f.id]} alt={f.filename} className="w-full max-h-72 object-contain bg-slate-100" />
                ) : (
                  <div className="h-32 flex items-center justify-center bg-slate-100"><Loader2 size={18} className="animate-spin text-slate-300" /></div>
                )}
                <div className="flex items-center gap-2 px-3 py-2">
                  <p className="text-xs text-slate-500 flex-1 truncate">{f.filename} · {f.uploaded_by_name}</p>
                  <button className="text-slate-400 hover:text-[#26788A]" onClick={() => downloadPhoto(f)} data-testid={`download-photo-${f.id}`}><Download size={15} /></button>
                  {(user?.role === "admin" || f.uploaded_by === user?.id) && (
                    <button className="text-slate-300 hover:text-red-500" onClick={async () => { if (window.confirm("Supprimer cette photo ?")) { await deleteMovementPhoto(f.id); openPhotos(photosMov); } }}><Trash2 size={14} /></button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
